import { isWechatAgent } from '@zelos/common';
import { useRequest } from 'ahooks';
import { SpinLoading } from 'antd-mobile';
import axios from 'axios';
import { createContext, lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import H5Config from './config';
import MainView from './routes/main';
import { getCurrentUserInfo, getPermissions } from './service';
import { zemClient } from './tracker';
import { RouteTracker } from './tracker/RouteTracker';
import { getQueryVariable, request, setStorage } from './utils';
import { partitionService } from './utils/request';

const TrailCar = lazy(() => import('./routes/trail-car'));
const Map = lazy(() => import('./routes/map'));
const Issues = lazy(() => import('./routes/issues'));
const Case = lazy(() => import('./routes/case'));
const ChongqingRace = lazy(() => import('./routes/chongqing-race'));
const DailyCheck = lazy(() => import('./routes/daily-check'));
const Transfer = lazy(() => import('./routes/data-transfer'));
const FlowView = lazy(() => import('./routes/flow'));
const ApproveView = lazy(() => import('./routes/flow/component/approve'));
const MessagesView = lazy(() => import('./routes/flow/component/messages'));
const OtaFlowView = lazy(() => import('./routes/flow/component/ota'));
const VehicleCaseFlowView = lazy(
  () => import('./routes/flow/component/vehicle-case'),
);
const Help = lazy(() => import('./routes/help'));
const MapCollect = lazy(() => import('./routes/map-collect'));
const MapCollectVehicleMode = lazy(
  () => import('./routes/map-collect-vehicle-mode'),
);
const ItemList = lazy(
  () => import('./routes/map-collect/detail/component/item-list-modal'),
);
const OfflineProblem = lazy(() => import('./routes/offline-problem'));
const PmView = lazy(() => import('./routes/pm'));
const Questionnaire = lazy(() => import('./routes/questionnaire'));
const RouteDelivery = lazy(() => import('./routes/route-delivery'));
const TakeoverReasonView = lazy(() => import('./routes/takeoverReason'));
const Page403 = lazy(() => import('./routes/page-403'));
const VehicleDelivery = lazy(() => import('./routes/vehicle-delivery'));
const PDFViewer = lazy(() => import('./routes/pdf-viewer'));
const Clue = lazy(() => import('./routes/clue'));
const RouteDeliveryNew = lazy(() => import('./routes/route-delivery-new'));

export const PermissionContext = createContext<string[]>([]);

const DELIVERY_APP_UA = 'com.zelostech.delivery';

function App() {
  const access_token = getQueryVariable('access_token');
  const login = request.ssoManager.isLogin() || !!access_token;

  const paddingTop = decodeURI(getQueryVariable('paddingTop'));

  useEffect(() => {
    if (paddingTop != null) {
      setStorage('paddingTop', paddingTop);
    }
  }, [paddingTop]);

  const [permissions, setPermissions] = useState<string[]>([]);

  const { run: getPermission } = useRequest(getPermissions, {
    manual: true,
    onSuccess: (res) => {
      if (res.success) {
        if (
          !res.data?.includes('H5App') &&
          window.location.pathname !== '/403' &&
          !window.location.pathname.startsWith('/questionnaire/')
        ) {
          window.location.href = '/403';
          return;
        }

        setPermissions(res.data);
      }
    },
  });

  const setUserInfo = ({
    mobileNumber,
    username,
    organizationId,
    userId,
    organizationName,
  }: {
    mobileNumber: string;
    username: string;
    organizationId: number | string;
    userId: number | string;
    organizationName?: string;
  }) => {
    localStorage.setItem('mobile_number', mobileNumber);
    localStorage.setItem('username', username);
    localStorage.setItem('organizationId', organizationId.toString());
    localStorage.setItem('userId', userId.toString());

    zemClient.identify({
      userId,
      userName: username,
      tenantId: organizationId,
      tenantName: organizationName,
    });

    if (window.apmPlus && userId) {
      window.apmPlus('config', { userId });
    }
  };

  useEffect(() => {
    if (access_token) {
      getCurrentUserInfo().then((res) => {
        const { mobileNumber, username, id, organization } = res.data;

        setUserInfo({
          mobileNumber,
          username,
          organizationId: organization?.id,
          userId: id,
          organizationName: organization?.name,
        });
      });
    }
  }, [access_token]);

  useEffect(() => {
    const _login = request.ssoManager.isLogin();
    const code = getQueryVariable('code');
    const grantType = getQueryVariable('grant_type');
    const access_token = getQueryVariable('access_token');
    const username = decodeURI(getQueryVariable('username'));
    const organizationId = getQueryVariable('organizationId');
    const userId = getQueryVariable('userId');
    const state = getQueryVariable('state');
    const mobile_number = getQueryVariable('mobileNumber');

    if (access_token) {
      localStorage.setItem('access_token', access_token);

      setUserInfo({
        mobileNumber: mobile_number,
        username,
        organizationId,
        userId,
      });

      return;
    }

    if (_login) {
      // 注入分区逻辑
      partitionService.initialize()?.initPartitions(true);

      getPermission();
      return;
    }

    if (code) {
      // 有code参数
      if (grantType) {
        axios
          .post(
            `https://${H5Config.getDerivedAppDomain('auth')}/oauth/token`,
            {
              client_id: 'mp-web',
              client_secret: 'mp-web@123456',
              grant_type: grantType,
              code,
            },
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
            },
          )
          .then((resp: any) => {
            const {
              user_name,
              access_token,
              refresh_token,
              user_id,
              mobile_number,
              organization_id,
              organization_name,
            } = resp.data;

            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token);

            setUserInfo({
              mobileNumber: mobile_number,
              username: user_name,
              organizationId: organization_id,
              userId: user_id,
              organizationName: organization_name,
            });

            // 处理url
            if (state) {
              window.location.href = decodeURIComponent(state);
            } else {
              window.location.reload();
            }
          });
      } else {
        request.ssoManager.login(code).then((res: any) => {
          const {
            user_name,
            user_id,
            organization_id,
            mobile_number,
            organization_name,
          } = res;

          setUserInfo({
            mobileNumber: mobile_number,
            username: user_name,
            organizationId: organization_id,
            userId: user_id,
            organizationName: organization_name,
          });

          // 处理url
          if (state) {
            window.location.href = decodeURIComponent(state);
          } else {
            window.location.reload();
          }
        });
      }
    } else {
      // 未登录
      // 微信登录
      if (isWechatAgent()) {
        window.location.href = `https://open${
          window.location.hostname.includes('-uat') ? '-uat' : ''
        }.zelostech.com.cn/wxlogin?redirect_uri=${window.location.href}`;
      } else {
        request.ssoManager.gotoLoginPage(true);
      }
    }
  }, [getPermission]);

  useEffect(() => {
    if (login) {
      // 注入分区逻辑
      partitionService.initialize()?.initPartitions(true);

      getPermission();
    }
  }, [login, getPermission]);

  if (!login) {
    return null;
  }

  return (
    <BrowserRouter>
      <RouteTracker>
        <PermissionContext.Provider value={permissions}>
          <Routes>
            <Route
              path="/"
              element={
                <MainView
                  fromApp
                  // fromApp={
                  //   // true
                  //   !!getQueryVariable('access_token') ||
                  //   window.navigator.userAgent.includes(DELIVERY_APP_UA)
                  // }
                />
              }
            />
            <Route
              path="/problem"
              element={<Navigate to="/issues/submit" replace />}
            />
            <Route
              path="/issues/*"
              element={
                <SuspenseWrapper>
                  <Issues />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/takeoverReason"
              element={
                <SuspenseWrapper>
                  <TakeoverReasonView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/pm"
              element={
                <SuspenseWrapper>
                  <PmView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/flow"
              element={
                <SuspenseWrapper>
                  <FlowView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/viewApprove"
              element={
                <SuspenseWrapper>
                  <ApproveView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/transfer/*"
              element={
                <SuspenseWrapper>
                  <Transfer />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/dailyCheck/*"
              element={
                <SuspenseWrapper>
                  <DailyCheck />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/map/*"
              element={
                <SuspenseWrapper>
                  <Map />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/help"
              element={
                <SuspenseWrapper>
                  <Help />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/vehicleCaseFlow"
              element={
                <SuspenseWrapper>
                  <VehicleCaseFlowView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/messageList"
              element={
                <SuspenseWrapper>
                  <MessagesView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/otaFlow"
              element={
                <SuspenseWrapper>
                  <OtaFlowView />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/offlineProblem"
              element={
                <SuspenseWrapper>
                  <OfflineProblem />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/trailCar"
              element={
                <SuspenseWrapper>
                  <TrailCar />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/mapCollect/*"
              element={
                <SuspenseWrapper>
                  <MapCollect />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/chongqingRace"
              element={
                <SuspenseWrapper>
                  <ChongqingRace />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/circleItems/:id"
              element={
                <SuspenseWrapper>
                  <ItemList />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/questionnaire/:id"
              element={
                <SuspenseWrapper>
                  <Questionnaire />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/mapCollectVehicleMode/*"
              element={
                <SuspenseWrapper>
                  <MapCollectVehicleMode />
                </SuspenseWrapper>
              }
            />
            {/* <Route
              path="/routeDelivery/*"
              element={
                <SuspenseWrapper>
                  <RouteDelivery />
                </SuspenseWrapper>
              }
            /> */}
            <Route
              path="/case"
              element={
                <SuspenseWrapper>
                  <Case />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/403"
              element={
                <SuspenseWrapper>
                  <Page403 fromApp={!!getQueryVariable('access_token')} />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/vehicleDelivery/*"
              element={
                <SuspenseWrapper>
                  <VehicleDelivery />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/pdf-viewer"
              element={
                <SuspenseWrapper>
                  <PDFViewer />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/clue/*"
              element={
                <SuspenseWrapper>
                  <Clue />
                </SuspenseWrapper>
              }
            />
            <Route
              path="/routeDelivery/*"
              element={
                <SuspenseWrapper>
                  <RouteDeliveryNew />
                </SuspenseWrapper>
              }
            />
          </Routes>
        </PermissionContext.Provider>
      </RouteTracker>
    </BrowserRouter>
  );
}

const SuspenseWrapper = (props: any) => {
  const loadingDom = (
    <div>
      <SpinLoading
        style={{
          '--size': '48px',
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        color="primary"
      />
    </div>
  );

  return (
    <Suspense fallback={loadingDom}>
      <>{props.children}</>
    </Suspense>
  );
};

export default App;
